import React, { useEffect, useState } from "react";
import { Layout, theme, Affix } from "antd";
import { useAuth } from "@/hooks/useAuth";
import { superadmin, higherOfficer, ownerMenus, adminItem } from "./contant";
import { useDispatch, useSelector } from "react-redux";
import { shipDetails } from "@/stores/slice/ownerslice/thunk";
import DisableInspect from "@/components/DisableInpectWindow";
import SidebarMenu from "./submenu";
import ServiceWorkerPopup from "@/ServiceWorkerPopup";
import MainHeader from "./header";
import superuserService from "@/services/superadminService";
import ChatbotWithHistory from "@/pages/Chat";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useFilteredMenuItems } from "@/hooks/useFilteredMenu";
import { selectPermissionStatus } from "@/stores/slice/permissionSlice";

const { Content, Footer } = Layout;

const MasterLayout = ({ children }) => {
  const { role } = useAuth();
  const dispatch = useDispatch();
  const [groupedCharts, setGroupedCharts] = useState({});
  const [chartTitles, setChartTitle] = useState({});
  const [collapsed, setCollapsed] = useState(true);
  const isMobile = typeof window !== "undefined" && window.innerWidth <= 991;

  const { token: { borderRadiusLG } } = theme.useToken();

  const SIDEBAR_WIDTH = 220;
  const COLLAPSED_WIDTH = 60;

  useEffect(() => { dispatch(shipDetails()); }, [dispatch, role]);

  function groupByGroupName(charts) {
    return charts.reduce((acc, chart) => {
      const g = chart.group?.group_name;
      if (g) { if (!acc[g]) acc[g] = []; acc[g].push(chart); }
      return acc;
    }, {});
  }

  const fetchChartData = async () => {
    try {
      const daily = await superuserService.getownersCharts("DAILY");
      const monthly = await superuserService.getownersCharts("MONTHLY");
      const d = daily?.data?.filter((x) => x?.variant !== "GENERAL") ?? [];
      const m = monthly?.data?.filter((x) => x?.variant !== "GENERAL") ?? [];
      setGroupedCharts(groupByGroupName([...d, ...m]));
      setChartTitle({
        DAILY: d.map((x) => ({ topic: x?.topic, title: x?.chart_title })).filter(Boolean),
        MONTHLY: m.map((x) => ({ topic: x?.topic, title: x?.chart_title })).filter(Boolean),
      });
    } catch (err) { console.log(err); }
  };

  useEffect(() => { if (role === "OWNER") fetchChartData(); }, [role]);

  const shallowClone = (items) =>
    items.map((item) => ({
      ...item,
      children: item.children ? item.children.map((c) => ({ ...c })) : undefined,
    }));

  const getDynamicOwnerMenus = () => {
    const list = shallowClone(ownerMenus);
    const chartMenu = list.find((m) => m.label === "Chart Reports");
    if (chartMenu?.children) {
      const groupedByType = { DAILY: {}, MONTHLY: {} };
      Object.entries(groupedCharts).forEach(([groupName, charts]) => {
        charts.forEach((chart) => {
          const t = chart.chart_reportType;
          if (!groupedByType[t]) groupedByType[t] = {};
          if (!groupedByType[t][groupName]) groupedByType[t][groupName] = [];
          groupedByType[t][groupName].push(chart);
        });
      });
      const d = chartMenu.children.find((c) => c.label === "Daily Report");
      const mo = chartMenu.children.find((c) => c.label === "Monthly Report");
      if (d) d.children = Object.entries(groupedByType.DAILY || {}).map(([name], i) => ({
        key: `daily-sub-${i}`, label: name, path: `/management/charts/DAILY/${name}`,
      }));
      if (mo) mo.children = Object.entries(groupedByType.MONTHLY || {}).map(([name], i) => ({
        key: `monthly-sub-${i}`, label: name, path: `/management/charts/MONTHLY/${name}`,
      }));
    }
    return list;
  };


  const filteredSuperadminMenus = useFilteredMenuItems(
    role === "SUPERADMIN" ? superadmin : []
  );
  const permStatus = useSelector(selectPermissionStatus);


  const getMenuItems = () => {
    switch (role) {
      case "OWNER": return getDynamicOwnerMenus();
      case "SUPERADMIN": return filteredSuperadminMenus; // ✅ use pre-computed value
      case "VESSEL_MANAGER":
      case "FLEET_MANAGER":
      case "OWNERGROUP": return adminItem;
      case "MASTER":
      case "CHIEF_ENGINEER": return higherOfficer;
      default: return [];
    }
  };

  const sidebarWidth = collapsed ? COLLAPSED_WIDTH : SIDEBAR_WIDTH;

  return (
    <Layout style={{ minHeight: "100vh", background: "#f0f2f5" }}>
      <Affix offsetTop={0} style={{ zIndex: 200 }}>
        <MainHeader chartTitles={chartTitles} />
      </Affix>

      <Layout>
        {/* ===== SIDEBAR ===== */}
        <div
          className={`custom-sidebar ${collapsed ? "sidebar-collapsed" : "sidebar-expanded"}`}
          style={{
            position: "fixed",
            top: 54,
            left: 0,
            width: sidebarWidth,
            height: "calc(100vh - 54px)",
            zIndex: 150,
            display: "flex",
            flexDirection: "column",
            background: "linear-gradient(160deg, #0a7c63 0%, #0d9276 40%, #00b894 100%)",
            boxShadow: "3px 0 16px rgba(0,0,0,0.18)",
            transition: "width 0.25s ease",
            overflowX: "visible",   /* allow popup to overflow */
            overflowY: "visible",
          }}
        >
          {/* Scrollable menu — overflow visible so popups escape */}
          <div style={{
            flex: 1,
            overflowY: "auto",
            overflowX: "visible",
            padding: "8px 0 0",
          }}>
            <SidebarMenu menuItems={getMenuItems()} collapsed={collapsed} />
          </div>

          {/* ===== TOGGLE BUTTON — sits on the right border edge ===== */}
          <div style={{
            position: "absolute",
            bottom: 20,               /* distance from bottom */
            right: -14,               /* half-outside the sidebar edge */
            zIndex: 160,
          }}>
            <button
              onClick={() => setCollapsed((c) => !c)}
              title={collapsed ? "Expand" : "Collapse"}
              style={{
                width: 28,
                height: 28,
                borderRadius: "50%",
                background: "linear-gradient(135deg, #0d9276, #00b894)",
                border: "2px solid white",
                boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                outline: "none",
                transition: "transform 0.2s, box-shadow 0.2s",
                padding: 0,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "scale(1.15)";
                e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.3)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "scale(1)";
                e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.25)";
              }}
            >
              {collapsed
                ? <ChevronRight size={14} color="white" strokeWidth={3} />
                : <ChevronLeft size={14} color="white" strokeWidth={3} />
              }
            </button>
          </div>
        </div>

        {/* Mobile overlay */}
        {!collapsed && isMobile && (
          <div
            onClick={() => setCollapsed(true)}
            style={{ position: "fixed", inset: 0, zIndex: 140, background: "rgba(0,0,0,0.4)" }}
          />
        )}

        {/* ===== MAIN CONTENT ===== */}
        <Layout style={{
          marginLeft: isMobile ? 0 : sidebarWidth,
          transition: "margin-left 0.25s ease",
          background: "#f0f2f5",
        }}>
          <Content style={{ padding: "8px", minHeight: "calc(100vh - 96px)" }}>
            <div style={{
              background: "white",
              borderRadius: borderRadiusLG,
              padding: "8px 12px",
              minHeight: "100%",
              boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
            }}>
              <ServiceWorkerPopup />
              {children}
            </div>
          </Content>
          <ChatbotWithHistory />
          <Footer style={{
            textAlign: "center", background: "#fff",
            padding: "8px 16px", borderTop: "1px solid #f0f0f0",
            fontSize: "12px", color: "#888",
          }}>
            Elan Project ©{new Date().getFullYear()} Created by Edkas
          </Footer>
        </Layout>
      </Layout>

      {import.meta.env.VITE_APP_ENV !== "DEV" && <DisableInspect />}
    </Layout>
  );
};

export default MasterLayout;